import { useTranslate } from "@refinedev/core";
import { message } from "antd";
import { Button, Popconfirm } from "@libertify/ui";
import type { UseMutationResult } from "@tanstack/react-query";
import type { MfaFactor } from "../types";
import { getMfaErrorDetail } from "../utils";

export interface DisableFactorButtonProps {
  factor: MfaFactor;
  disableFactor: UseMutationResult<unknown, unknown, MfaFactor>;
  // Called after a successful disable so the parent collapse panel closes.
  onCollapse: () => void;
}

// Shared "disable" confirm + button for an enabled MFA factor — identical
// across factor types, just parameterized by which factor to disable.
export const DisableFactorButton = ({
  factor,
  disableFactor,
  onCollapse,
}: DisableFactorButtonProps) => {
  const t = useTranslate();
  // disableFactor is shared across both factor cards (see TwoFactorAuthSection),
  // so isPending alone would show both as loading. Scope it to this factor via
  // the variables the in-flight mutation was actually called with.
  const isPending =
    disableFactor.isPending && disableFactor.variables === factor;

  const handleDisable = () => {
    disableFactor.mutate(factor, {
      onSuccess: onCollapse,
      onError: (error) => {
        // Shouldn't happen from our own UI — surface it loudly.
        console.error(`Failed to disable the ${factor} factor:`, error);
        message.error(
          getMfaErrorDetail(error)?.message ||
            t("pages.settings.security.genericError")
        );
      },
    });
  };

  return (
    <Popconfirm
      title={t("pages.settings.security.disableConfirmTitle")}
      description={t("pages.settings.security.disableConfirmDescription")}
      confirmText={t("pages.settings.security.disable")}
      cancelText={t("buttons.cancel")}
      onConfirm={handleDisable}
      danger
      loading={isPending}
    >
      <Button variant="danger" disabled={isPending}>
        {t("pages.settings.security.disable")}
      </Button>
    </Popconfirm>
  );
};
